import React, {ReactElement, ReactNode} from "react";
import Cookies from "js-cookie";
import {Helmet} from "react-helmet";
import {emitter} from "@marvelapp/react-ab-test";

import FooterWrapper from "../../footer/FooterWrapper";
import GDPRContext, {GDPRContextState} from "/src/contexts/GDPRContext";

// Lazy load EmailSubscribe to avoid bundling form components on all pages to reduce bundle size
const EmailSubscribe = React.lazy(
  () => import("../../email-subscribe/EmailSubscribe")
);
import Header from "../../header-nav/header/Header";
import SimpleHeader from "../../header-nav/simple-header/SimpleHeader";
import useHasWindow from "/src/hooks/useHasWindow";

import useSuperMainNavBar from "../../../hooks/useSuperMainNavBar";
import {useSiteMetaData} from "/src/hooks/useSiteMetaData";
import {MaybeType} from "/src/types";
import {css} from "/src/utils/utils";
import {LOCALE} from "/src/config";
import {EMAIL_SUBSCRIBE_CN} from "/src/data/email-subscribe";
import translate from "/src/utils/translate";
import {updateActiveExperiments} from "/src/utils/utils";

import * as styles from "./layout.module.scss";

export enum HEADER_FOOTER_VISIBILITY {
  BOTH = "BOTH",
  FOOTER_ONLY = "FOOTER_ONLY",
  HEADER_ONLY = "HEADER_ONLY",
  NONE = "NONE",
}

export interface LayoutProps {
  children: ReactNode;
  promoBannerData?: MaybeType<Queries.PromoBannerFragmentFragment>;
  headerAndFooterVisibility?: HEADER_FOOTER_VISIBILITY;
  stickyHeader?: boolean;
  simpleHeaderProductName?: MaybeType<string>;
  simpleHeaderRightNavItems?: ReactNode;
  headerLink?: string;
  hideNavSearch?: boolean;
  hideNotificationBar?: boolean;
  hideRightNav?: boolean;
  contentClasses?: string;
}

/*
 * This is a catchall function that listens for chosen variants to be displayed to the user.
 * Meaning for any given experiment, one variant is chosen and then the current active experiments are
 * updated with their appropriate variants
 */
emitter.addActiveVariantListener(
  (experimentName: string, variantName: string) => {
    updateActiveExperiments(experimentName, variantName);
  }
);

export const LayoutContext = React.createContext<Record<string, any>>({
  hideNavSearch: false,
  hideRightNav: false,
});

// separating this as micro component to perform partial hydation for now
// TODO: go through each component and separate out server and client content
// ref: https://www.gatsbyjs.com/docs/how-to/performance/partial-hydration/#when-to-use-client-components
const Content = (props: LayoutProps): ReactElement | null => {
  const gdpr = React.useContext<GDPRContextState>(GDPRContext);
  const {countryCode} = gdpr;
  const hasWindow = useHasWindow();

  if (!hasWindow) {
    return null;
  }

  const {headerAndFooterVisibility = HEADER_FOOTER_VISIBILITY.BOTH} = props;

  const hideFooterNav =
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.NONE ||
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.HEADER_ONLY;

  return (
    <>
      {(LOCALE === "zh-CN" || countryCode === "CN") && !hideFooterNav ? (
        <React.Suspense fallback={null}>
          <EmailSubscribe {...EMAIL_SUBSCRIBE_CN} />
        </React.Suspense>
      ) : null}
    </>
  );
};

export default function Layout(props: LayoutProps) {
  const {
    headerAndFooterVisibility = HEADER_FOOTER_VISIBILITY.BOTH,
    stickyHeader = false,
    promoBannerData,
    simpleHeaderProductName,
    headerLink,
    hideNavSearch,
    hideNotificationBar = false,
    hideRightNav = false,
    children,
    simpleHeaderRightNavItems,
    contentClasses,
  } = props;
  const superMainNavBars: Queries.SuperMainNavBarsQueryQuery["contentfulSuperMainNavBars"] =
    useSuperMainNavBar();

  const siteMetadata = useSiteMetaData();

  // let dismissPromoCookieName = "";
  const dismissPromoCookieName = React.useRef("");

  const [showPromoBanner, setShowPromoBanner] = React.useState(false);

  const handlePromoBannerClose = () => {
    // The header needs to know when the promo bar is closed for recalculating sticky position.
    Cookies.set(dismissPromoCookieName.current, "true", {expires: 21});
    setShowPromoBanner(false);
  };

  React.useEffect(() => {
    dismissPromoCookieName.current = `dismiss-${promoBannerData?.title || ""}`;

    const isPromoBannerEnabled = promoBannerData?.visible || false;

    const hasUserDismissPromoBanner =
      Cookies.get(dismissPromoCookieName.current) === "true";

    setShowPromoBanner(isPromoBannerEnabled && !hasUserDismissPromoBanner);
  }, [promoBannerData?.title, promoBannerData?.visible]);

  const headerVisible =
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.BOTH ||
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.HEADER_ONLY;

  const hideFooterNav =
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.NONE ||
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.HEADER_ONLY;

  const hideFooterPrivacyPolicy =
    headerAndFooterVisibility === HEADER_FOOTER_VISIBILITY.HEADER_ONLY;

  const newNavIsRenderedIn = superMainNavBars?.newNavIsRenderedIn as MaybeType<
    Array<string>
  >;

  return (
    <LayoutContext.Provider value={{hideNavSearch, hideRightNav}}>
      <div className={styles.layout}>
        {/*
          skip to content placed here instead of header,
          as there is a possibility header will not be rendered
        */}
        <a
          href="#flexport-main-content"
          className={styles.layout__skipToContent}
        >
          {translate("Skip to content")}
        </a>

        <Helmet>
          <title>{siteMetadata.title}</title>
          <meta name="description" content={siteMetadata.description} />
        </Helmet>

        {headerVisible && (
          <Header
            handlePromoBannerClose={handlePromoBannerClose}
            promoBannerData={promoBannerData}
            isPromoBannerVisible={showPromoBanner}
            isSticky={stickyHeader}
            newNavIsRenderedIn={newNavIsRenderedIn}
            hideNotificationBar={hideNotificationBar}
          />
        )}

        {!headerVisible && simpleHeaderProductName != null && (
          <SimpleHeader
            rightNav={simpleHeaderRightNavItems}
            productName={simpleHeaderProductName}
            headerLink={headerLink}
          />
        )}

        <main
          className={css(
            "content-inertable",
            styles.layout__mainContent,
            contentClasses!
          )}
          id="flexport-main-content"
        >
          {children}
          <Content {...props} />
        </main>
        {/*
         * We render a Footer with visibility=WITHOUT_NAV because:
         * (1) There's no Header to show Flexport's Terms & Conditions and Privacy Policy.
         * (2) We are legally required to show Google's reCAPTCHA Terms & Conditions.
         * (3) We don't want to display the large navigation columns.
         */}
        <FooterWrapper
          hideNav={hideFooterNav}
          hidePrivacyPolicy={hideFooterPrivacyPolicy}
          newNavIsRenderedIn={newNavIsRenderedIn}
        />
      </div>
    </LayoutContext.Provider>
  );
}
